import { motion } from "framer-motion";
import { Download, ClipboardList, Clapperboard, GraduationCap, Lock, LogOut } from "lucide-react";
import { ASSETS } from "@/lib/assets";
import { useAuth } from "@/context/AuthContext";

const experience = [
  { role: "Owner / Freelance Producer", org: "DBoye Creatives Inc", dates: "2015 — Present" },
  { role: "Makerspace Program Implementer", org: "Southern Africa Philanthropy Foundation · American Corner CPT", dates: "2023 — Present" },
  { role: "Festival Technical Manager", org: "Encounters Intl. Documentary Film Festival", dates: "2019 — 2024" },
  { role: "Makerspace Executive Producer", org: "American Corner Cape Town", dates: "2018 — 2023" },
  { role: "Social Media & Event Support", org: "U.S. Consulate General CPT · EducationUSA · SAFFI", dates: "2018 — 2023" },
  { role: "Head of Technical Department / Studio", org: "BigFish School of Digital Filmmaking", dates: "2019 — 2020" },
];

const skills = [
  "Project Management", "Live Broadcast & Streaming", "Cinematography / DOP", "Directing",
  "Video & Digital Editing", "Animation / VFX", "Graphic Design", "Creative Marketing Strategy",
  "Program Development & Facilitation", "Web · SEO · Analytics", "Monitoring & Evaluation",
];

const education = [
  { name: "African Civic Engagement Academy", detail: "Civic engagement, media strategy, nonprofit & project management", year: "2022" },
  { name: "Triggerfish · Disney · AFS", detail: "African Animation Masterclass Series", year: "2022" },
  { name: "SAE Institute Cape Town", detail: "Higher Certificate — Digital Film Production", year: "2017–19" },
  { name: "SAE Institute Cape Town", detail: "Higher Certificate — Animation & Visual Effects", year: "2015–17" },
];

const clients = ["DISNEY", "TRIGGERFISH", "U.S. CONSULATE", "EDUCATIONUSA", "SAPF", "AMERICAN CORNER", "ENCOUNTERS FESTIVAL", "CITY OF CAPE TOWN", "SAFFI", "SAE INSTITUTE"];

const fade = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: i * 0.05 } }),
};

export const About = () => {
  const { user, requestResume, logout } = useAuth();
  return (
    <section id="about" data-testid="about-section" className="py-28 sm:py-40 bg-[#0A0A0A]">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: portrait + identity + CV */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <div className="relative overflow-hidden border border-white/10 aspect-[4/5]">
                <img src={ASSETS.portraitLowAngle} alt="Darrel Boye on the rig" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              </div>
              <p className="font-body text-xs uppercase tracking-[0.3em] text-gray-500 mt-8 mb-3">About</p>
              <h2 className="font-head uppercase text-4xl sm:text-5xl tracking-tighter text-white leading-none">
                Darrel Boye
              </h2>
              <p className="font-serif-italic text-lg text-[#EF233C] mt-3">
                Producer · Cinematographer · Creative Strategist
              </p>
              <button
                onClick={requestResume}
                data-testid="download-cv-btn"
                className="group mt-8 inline-flex items-center gap-4 px-8 py-4 bg-[#D90429] hover:bg-[#EF233C] transition-colors duration-300"
              >
                {user ? (
                  <Download className="w-4 h-4 text-white group-hover:translate-y-0.5 transition-transform" />
                ) : (
                  <Lock className="w-4 h-4 text-white" />
                )}
                <span className="font-body uppercase tracking-[0.15em] text-sm text-white">
                  {user ? "Download CV / Resume" : "Sign in with Google to Download my CV"}
                </span>
              </button>
              {user && (
                <div data-testid="cv-signed-in" className="mt-4 flex items-center gap-3 text-sm text-gray-400">
                  <span>Signed in as <span className="text-white">{user.name || user.email}</span></span>
                  <button
                    onClick={logout}
                    data-testid="cv-logout-btn"
                    className="inline-flex items-center gap-1.5 text-gray-500 hover:text-[#EF233C] transition-colors"
                  >
                    <LogOut className="w-3.5 h-3.5" /> Log out
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right: content */}
          <div className="lg:col-span-7">
            <p className="font-body text-base sm:text-lg text-gray-300 leading-relaxed">
              Versatile freelance film & animation producer and creative marketing strategist, and the driving force
              behind DBoye Creatives Inc — a Cape Town video production and non-profit company with roots in Accra,
              Ghana and strong ties to Los Angeles. From the Story Expo in New York to the American Film Market, Darrel
              blends technical mastery with a mission to train youth and exchange skills across Africa and the global
              filmmaking community.
            </p>

            {/* Project management highlight */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              data-testid="pm-highlight"
              className="mt-10 border border-[#D90429]/40 bg-[#0f0f10] p-8"
            >
              <span className="flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[#EF233C] mb-4">
                <ClipboardList className="w-4 h-4" /> Project Management
              </span>
              <p className="font-body text-base text-gray-300 leading-relaxed">
                End-to-end producer and technical lead across live broadcasts, film festivals, and multi-partner
                non-profit programs. As Festival Technical Manager for Encounters and Executive Producer at the American
                Corner Makerspace, Darrel plans budgets, coordinates crews and stakeholders, manages equipment and
                timelines, and delivers on deadline — formally trained in nonprofit & project management via the African
                Civic Engagement Academy.
              </p>
            </motion.div>

            {/* Experience */}
            <h3 className="font-head uppercase text-2xl tracking-tight text-white mt-14 mb-6 flex items-center gap-3">
              <Clapperboard className="w-5 h-5 text-[#EF233C]" /> Experience
            </h3>
            <div className="divide-y divide-white/10 border-t border-white/10">
              {experience.map((e, i) => (
                <motion.div
                  key={i}
                  custom={i}
                  variants={fade}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  data-testid={`experience-${i}`}
                  className="py-5 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1"
                >
                  <div>
                    <p className="font-head uppercase text-lg text-white tracking-tight leading-tight">{e.role}</p>
                    <p className="font-body text-sm text-gray-400">{e.org}</p>
                  </div>
                  <span className="font-body text-xs uppercase tracking-[0.15em] text-gray-500 shrink-0">{e.dates}</span>
                </motion.div>
              ))}
            </div>

            {/* Skills */}
            <h3 className="font-head uppercase text-2xl tracking-tight text-white mt-14 mb-6">Capabilities</h3>
            <div className="flex flex-wrap gap-3">
              {skills.map((s) => (
                <span
                  key={s}
                  data-testid={`skill-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`font-body text-sm px-4 py-2 border transition-colors ${
                    s === "Project Management"
                      ? "border-[#D90429] text-white bg-[#D90429]/15"
                      : "border-white/15 text-gray-300 hover:border-white/40"
                  }`}
                >
                  {s}
                </span>
              ))}
            </div>

            {/* Education */}
            <h3 className="font-head uppercase text-2xl tracking-tight text-white mt-14 mb-6 flex items-center gap-3">
              <GraduationCap className="w-5 h-5 text-[#EF233C]" /> Education & Training
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {education.map((ed, i) => (
                <div key={i} data-testid={`education-${i}`} className="border border-white/10 p-5">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="font-head uppercase text-base text-white tracking-tight leading-tight">{ed.name}</p>
                    <span className="font-body text-xs text-gray-500 shrink-0">{ed.year}</span>
                  </div>
                  <p className="font-body text-sm text-gray-400 mt-2">{ed.detail}</p>
                </div>
              ))}
            </div>

            {/* Clients */}
            <h3 className="font-head uppercase text-2xl tracking-tight text-white mt-14 mb-6">Trusted By</h3>
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              {clients.map((c) => (
                <span key={c} className="font-head uppercase text-sm tracking-[0.15em] text-gray-500">
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
